import type {AuditModule} from '@/features/bulk-upload/models/bulk-upload-log.model';

export interface ExportAuditPayload {
    serviceName?: string | null;
    moduleName?: string | null;
    fileName?: string | null;
    result?: string | null;
    totalRecords: number;
    duration: number;
}

export type ExportAuditLogger = (payload: ExportAuditPayload) => void;

let registeredLogger: ExportAuditLogger | null = null;

export function setExportAuditLogger(logger: ExportAuditLogger | null): void {
    registeredLogger = logger;
}

export function recordExportAudit(payload: ExportAuditPayload): void {
    if (!registeredLogger) return;
    try {
        registeredLogger(payload);
    } catch {
        // Audit failures must never break the export flow.
    }
}

export function deriveModuleNameFromFilename(filename: string): string {
    const base = filename.replace(/\.[a-z0-9]+$/i, '');
    const slug = base.replace(/^[\d_-]+/, '').replace(/[_-]+/g, ' ').trim();
    if (!slug) return base || filename;
    return slug.replace(/\b\w/g, (c) => c.toUpperCase());
}

/**
 * The backend `AuditModule` name (`bulk-upload-log.model.ts`) recorded as the `moduleName` of a list's export, by the
 * export file name (without extension).
 */
const AUDIT_MODULE_BY_EXPORT_FILE: Record<string, keyof typeof AuditModule> = {
    'projects': 'Project',
    'boq': 'Boq',
    'sites': 'Site',
    'sub-contractor-categories': 'SubcontractorCategory',
    'subcontractors': 'Subcontractor',
    'users': 'User',
    'employees': 'Employee',
    'employee-project-mapping': 'EmployeeProjectMapping',
    'unit-master': 'Unit',
    'material-categories': 'MaterialCategory',
    'material-sub-categories': 'MaterialSubCategory',
    'consumption-categories': 'ConsumptionCategory',
    'materials': 'Material',
    'material-mapping': 'MaterialMapping',
    'material-inward': 'MaterialInward',
    'consumption': 'Consumption',
    'salary': 'Salary',
    'language-translations': 'LanguageTranslation',
    'project-cost-summary': 'ProjectCostSummaryReport',
    'boq-profit-loss-report': 'BoqProfitLossReport',
    'material-stock-report': 'MaterialStockReport',
    'contractor-cost-report': 'SubcontractorCostReport',
    'consumption-report': 'ConsumptionReport',
    'material-consumption-report': 'MaterialConsumptionReport',
    'employee-project-assignment-report': 'EmployeeProjectMappingReport',
};

/** The export's `AuditModule` name; `fallback` (the report title) for exports without a module (logs, user list, ...). */
export function auditModuleName(filename: string, fallback: string): string {
    return AUDIT_MODULE_BY_EXPORT_FILE[filename.replace(/\.[a-z0-9]+$/i, '')] ?? fallback;
}

/** Service name recorded for an export whose route isn't in EXPORT_SERVICE_BY_ROUTE. */
export const DEFAULT_EXPORT_SERVICE = 'PMCC_MasterService';

/** Maps the first URL segment (feature route) to the backend service name recorded in export audits. */
export const EXPORT_SERVICE_BY_ROUTE: Record<string, string> = {
    // 'orders': 'OrderManagement',
};

export function deriveServiceNameFromUrl(): string | null {
    if (typeof window === 'undefined') return null;
    const segments = window.location.pathname.split('/').filter(Boolean);
    if (!segments.length) return null;
    const first = segments[0].toLowerCase();
    return EXPORT_SERVICE_BY_ROUTE[first] ?? null;
}
